import { Navigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../store/store';
import { useEffect, useState } from 'react';
import { isValidCohort } from '../services/auth';

// ORA-1143: this is the catch-all route, so unknown paths (e.g. `/login`) land
// here too. Only a real school cohort may start the cohort sign-up flow;
// a slug the backend says is unknown goes to the plain auth page and clears
// any copy of itself left in storage. A check that errors or times out falls
// back to the pre-check behaviour (sign-up rejects unknown cohorts anyway), so
// a flaky network never costs a real school link its attribution.
const VALIDITY_CHECK_TIMEOUT_MS = 5000;

type Validity = 'valid' | 'invalid' | 'unknown';

const checkCohortValidity = async (cohortId: string): Promise<Validity> => {
  let timer: ReturnType<typeof setTimeout> | undefined;

  const deadline = new Promise<Validity>((resolve) => {
    timer = setTimeout(() => resolve('unknown'), VALIDITY_CHECK_TIMEOUT_MS);
  });

  try {
    return await Promise.race([
      isValidCohort(cohortId).then((valid): Validity =>
        valid ? 'valid' : 'invalid'
      ),
      deadline,
    ]);
  } catch {
    return 'unknown';
  } finally {
    clearTimeout(timer);
  }
};

const storeCohort = (cohortId: string) => {
  try {
    localStorage.setItem('ora-cohort', cohortId);
  } catch {
    // Blocked site data: the cohort still rides the `/auth?cohort=` param.
  }
};

const clearStoredCohortIfMatches = (cohortId: string) => {
  try {
    const stored = localStorage.getItem('ora-cohort');
    if (stored && stored.toLowerCase() === cohortId.toLowerCase()) {
      localStorage.removeItem('ora-cohort');
      localStorage.removeItem('ora-cohort-link');
    }
  } catch {
    // Blocked site data: nothing was stored to clear.
  }
};

const CohortRedirect = () => {
  const isAuthenticated = useSelector(
    (state: RootState) => state.auth.isAuthenticated
  );

  const { cohortId } = useParams<{ cohortId: string }>();

  // Keyed by cohort so a stale result never applies after a param change.
  const [validity, setValidity] = useState<{
    cohortId: string;
    validity: Validity;
  } | null>(null);

  useEffect(() => {
    if (isAuthenticated || !cohortId) return;

    let isMounted = true;

    void checkCohortValidity(cohortId).then((result) => {
      if (!isMounted) return;
      if (result === 'invalid') {
        clearStoredCohortIfMatches(cohortId);
      } else {
        storeCohort(cohortId);
      }
      setValidity({ cohortId, validity: result });
    });

    return () => {
      isMounted = false;
    };
  }, [isAuthenticated, cohortId]);

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  if (!cohortId) {
    return <Navigate to="/auth" replace />;
  }

  if (validity?.cohortId !== cohortId) {
    return null;
  }

  if (validity.validity === 'invalid') {
    return <Navigate to="/auth" replace />;
  }

  const params = new URLSearchParams();
  params.set('cohort', cohortId);
  params.set('redirect', '/');

  return <Navigate to={`/auth?${params.toString()}`} replace />;
};

export default CohortRedirect;
